import {ACTIVITY_TYPE, ActivityRequestTypes, AllActivityTypes, AllActivityTypesArray} from "../../types-enums/activity";
import {
    BaseCreateOrUpdateBundleActivity,
    BundleForStore,
    CreateBundleProps,
    CreateOrUpdateCarBusActivity,
    CreateOrUpdatedBundledActivities,
    CreateOrUpdatedFlightActivity,
    CreateOrUpdatedHotelActivity,
    LoadBundleFlightActivity,
    LoadBundleResponse,
    LoadedActivities,
    LoadedActivity,
    UpdateBundle
} from "../../api/model/bundle";
import {v4 as uuidv4} from "uuid";
import {
    isActivityCarBusState,
    isActivityFlightState,
    isActivityHotelState,
    isFlightCalculationRequestLeg,
    isHotelCalculationRequest,
    isLoadBundleCarBusActivity,
    isLoadBundleFlightActivity,
    isLoadBundleHotelActivity,
    isLoadedActivity,
    isUpdateBundle
} from "../../utils/check/type-guards";
import {hasExactDates} from "../../utils/getter/journey";
import {
    convertBackendStringDateToDateTime,
    convertDateToBackendStringDate,
    convertToDurationProps
} from "../../utils/converter/dates";
import {HotelScope} from "../../api/model/activity-hotel";
import {groupBy, isEqual} from "lodash";
import {CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL, CALCULATION_TYPE} from "../../api/model/common";
import {ActivityFlightState, Leg} from "../../types-enums/activity-flight";
import {convertEnumHotelClassToStringTravelClass} from "../../utils/converter/activities";
import {
    setCommonCarBusActivityProps,
    setCommonFlightActivityProps,
    setCommonHotelActivityProps
} from "../../utils/setter/activities";
import {
    CarBusCalculatedEmissionsResponse,
    FlightCalculatedEmissionsResponse,
    HotelCalculatedEmissionsResponse
} from "../../api/model/emissions";
import {getDurationInDaysOrNights} from "../../utils/getter/common";
import {ActivityHotelState} from "../../types-enums/activity-hotel";
import {createArrayFromNumber} from "../../utils/common";
import {completeInitialCustomer} from "../../components/forms/customer-form/CustomerForm";
import {store} from "./store";
import {CarBusCalculationRequest, VEHICLE_TYPE} from "../../api/model/activity-car-bus";
import {getCalculatedOrManualEnteredDistanceKm} from "../../utils/getter/car-bus";
import {ActivityCarBusState} from "../../types-enums/activity-car-bus";
import {getActivitiesByActivityGroupId, getReturnActivity} from "../../utils/getter/activities";
import {
    getHotelClassNumberValueForCalculationRequest,
    getStartAndEndDurationOfHotelStay
} from "../../utils/getter/hotel";
import {DateTime} from "luxon";
import {INITIAL_WAYPOINTS} from "../../components/activity/car-bus/ChooseCarBus";
import {
    getStartOrEndDayOfGroupedActivity,
    START_OR_END_OF_GROUP
} from "../../components/journey/journey-overview/common";
import cloneDeep from "lodash/cloneDeep";
import {setDurationOfHotelToOneWhenAddingToJourney} from "../../utils/converter/hotel";
import {DurationProps} from "../../types-enums/activity-common-properties";

interface TotalCo2KgProps {
    totalCo2KgE: number
}

const convertLoadedCarsBussesToFrontEntCarsBusses = (
    activities: LoadedActivities,
    activitiesForStore: AllActivityTypesArray,
    journeyDuration: DurationProps | null
) => {
    activities.forEach(carBus => {
        if (isLoadBundleCarBusActivity(carBus)) {
            const {
                calculatedDistanceKm,
                manualEnteredDistanceKm,
                vehicleType,
                fuelType,
                fuelConsumption,
                daysOfUse,
                busCategory,
                totalPassengerCount,
                numberOfPassengers,
                carInfo,
                emissions,
                id,
                day,
                route,
                waypoints,
                previousEmissionsCo2KgE,
                isHybrid,
            } = carBus
            let convertedWaypoints = waypoints
            if (waypoints === null) convertedWaypoints = INITIAL_WAYPOINTS
            let totalEmissions: CarBusCalculatedEmissionsResponse
            let originalEmissions: CarBusCalculatedEmissionsResponse
            if (emissions === null) {
                totalEmissions = {
                    index: 1,
                    status: CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.failed,
                    emissionKgCO2e: null,
                    errorCodes: null,
                    errors: null,
                    type: CALCULATION_TYPE.car,
                }
                originalEmissions = cloneDeep(totalEmissions)
            } else {
                originalEmissions = cloneDeep(emissions)
                const emissionsAdjustedToDuration = adjustEmissionsAccordingToDurationInRequest(carBus, ACTIVITY_TYPE.car_bus)
                totalEmissions = {
                    ...emissions,
                    emissionKgCO2e: emissionsAdjustedToDuration,
                }
            }
            const originalRequest: ActivityRequestTypes[] = [
                {
                    index: 1,
                    vehicleType,
                    totalPassengerCount,
                    numberOfPassengers,
                    fuelConsumption,
                    distance: getCalculatedOrManualEnteredDistanceKm(manualEnteredDistanceKm, calculatedDistanceKm),
                    daysOfUse: vehicleType === VEHICLE_TYPE.car ? daysOfUse : null,
                    fuelType,
                    carInfo,
                    busCategory,
                    isHybrid,
                    type: CALCULATION_TYPE.car
                }
            ]
            let groupId: string | null = null
            if (daysOfUse !== null && daysOfUse > 1) groupId = uuidv4()
            const carBusState: ActivityCarBusState = {
                common: {
                    ...setCommonCarBusActivityProps(),
                    day,
                    editId: null,
                    hideLoadBundleInformation: false,
                    activityType: ACTIVITY_TYPE.car_bus,
                    totalEmissions: [totalEmissions],
                    originalTotalEmissions: [originalEmissions],
                    id: uuidv4(),
                    semanticId: uuidv4(),
                    groupId,
                    originalRequest,
                    request: originalRequest
                },
                isHybrid,
                calculatedDistanceKm,
                manualEnteredDistanceKm,
                vehicleType,
                fuelType,
                fuelConsumption,
                countDays: daysOfUse ?? 1,
                busCategory,
                totalPassengerCount,
                passengerCount: numberOfPassengers,
                carCategory: carInfo?.carCategory ?? null,
                acrissCode: carInfo?.acrissCode ? `${carInfo.acrissCode.firstCharacter}${carInfo.acrissCode.lastCharacter}` : null,
                informationFromLoadedBundle: {previousEmissionsCo2KgE, countDays: daysOfUse},
                route,
                originalWaypoints: waypoints,
                originalRoute: route,
                originalCountDays: daysOfUse,
                waypoints: convertedWaypoints,
                backendId: id,
            }

            if (daysOfUse !== null && day !== null) {
                updateActivityIdsBasedOnDuration(carBusState, daysOfUse, journeyDuration).forEach(updatedCarBusState => activitiesForStore.push(updatedCarBusState))
            } else {
                activitiesForStore.push(carBusState)
            }
        }
    })
}

export const generateResendInvitationEmailUrl = (email: string, agencyId: number | null) => {
    const BASE_URL = "/api/v1/user/foreign/password/reset"
    console.log(agencyId)
    console.log(`${BASE_URL}/${email}`)
    if (agencyId === null) return `${BASE_URL}/${email}`
    return `${BASE_URL}/${email}/${agencyId}`
}

/**
 * Divides the emissions according to the duration used in the calculation request
 */
const adjustEmissionsAccordingToDurationInRequest = (activity: LoadedActivity, activityType: ACTIVITY_TYPE) => {
    if (isLoadedActivity(activity)) {
        const {emissions} = activity
        if (emissions !== null && emissions.emissionKgCO2e !== null) {
            let durationUsedInCalculation = 1
            switch (activityType) {
                case ACTIVITY_TYPE.car_bus:
                    if (isLoadBundleCarBusActivity(activity)) durationUsedInCalculation = activity.daysOfUse ?? 1
                    break;
                case ACTIVITY_TYPE.hotel:
                    if (isLoadBundleHotelActivity(activity)) durationUsedInCalculation = activity.numberNights
                    break;
                case ACTIVITY_TYPE.flight:
                    durationUsedInCalculation = 1
                    break
                default:
                    activityType satisfies never
            }
            return emissions.emissionKgCO2e / durationUsedInCalculation
        }
        return null
    }
    return null
}

/**
 * Transforms the persisted hotels into the data structure needed by the front end. Note that in the backend one hotel
 * is persisted as one row, regardless of the length of the stay. However, the frontend needs a hotel object for every
 * night of the stay.
 */
const convertLoadedHotelsToFrontEndHotels = (
    activities: LoadedActivities,
    activitiesForStore: AllActivityTypesArray,
    journeyDuration: DurationProps | null
) => {
    activities.forEach(hotel => {
        if (isLoadBundleHotelActivity(hotel)) {
            const {
                day, emissions, startDate, endDate, numberNights, hotelClass,
                giataId, hotelName, city, region, countryName, countryAlpha2,
                roomCategory, countryAlpha3, numberRooms, mealPlan, numberPeople,
                hotelDiscontinued, originalHotelName, id, previousEmissionsCo2KgE,
                originalHotelClass
            } = hotel
            let totalEmissions: HotelCalculatedEmissionsResponse
            let originalEmissions: HotelCalculatedEmissionsResponse
            if (emissions === null) {
                totalEmissions = {
                    index: 1,
                    status: CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.failed,
                    emissionKgCO2e: null,
                    errorCodes: null,
                    errors: null,
                    type: CALCULATION_TYPE.hotel,
                    scope: HotelScope.country
                }
                originalEmissions = cloneDeep(totalEmissions)
            } else {
                originalEmissions = cloneDeep(emissions)
                const emissionsAdjustedToDuration = adjustEmissionsAccordingToDurationInRequest(hotel, ACTIVITY_TYPE.hotel)
                totalEmissions = {
                    ...emissions,
                    emissionKgCO2e: emissionsAdjustedToDuration,
                }
            }
            const originalRequest: ActivityRequestTypes[] = [
                {
                    index: 1,
                    hotelId: hotel.giataId,
                    hotelName: hotel.hotelName,
                    city: hotel.city,
                    region: hotel.region,
                    country: hotel.countryAlpha3,
                    hotelClass: getHotelClassNumberValueForCalculationRequest(hotel.hotelClass),
                    roomCategory: hotel.roomCategory,
                    numberOfRooms: hotel.numberRooms,
                    numberOfNights: hotel.numberNights,
                    numberOfPeople: hotel.numberPeople,
                    mealPlan: hotel.mealPlan,
                    type: CALCULATION_TYPE.hotel
                }
            ]
            const hotelStayDuration = convertToDurationProps(startDate, endDate, numberNights)!!
            const hotelDurationInNights = getDurationInDaysOrNights(hotelStayDuration, true)
            let groupId: string | null = null
            if (hotelDurationInNights !== null && hotelDurationInNights > 1) groupId = uuidv4()
            const hotelState: ActivityHotelState = {
                common: {
                    ...setCommonHotelActivityProps(),
                    day,
                    editId: null,
                    hideLoadBundleInformation: false,
                    activityType: ACTIVITY_TYPE.hotel,
                    totalEmissions: [totalEmissions],
                    originalTotalEmissions: [originalEmissions],
                    id: uuidv4(),
                    semanticId: uuidv4(),
                    groupId,
                    originalRequest,
                    request: originalRequest
                },
                hotelStayDuration,
                hotelClass,
                backendId: id,
                hotel: hotelName !== null ? {
                    id: 1,
                    hotelId: giataId,
                    name: hotelName,
                    city,
                    region,
                    alpha2: countryAlpha2,
                    alpha3: countryAlpha3,
                    countryName,
                    stars: convertEnumHotelClassToStringTravelClass(hotelClass)!!
                } : null,
                city: {
                    id: 1,
                    name: city,
                    region,
                    alpha2: countryAlpha2,
                    alpha3: countryAlpha3,
                    countryName: countryName
                },
                country: {
                    id: 1,
                    name: countryName,
                    alpha2: countryAlpha2,
                    alpha3: countryAlpha3,
                },
                roomCategory,
                numberOfRooms: numberRooms,
                guestCount: numberPeople,
                mealPlan,
                originalHotelStayDuration: hotelStayDuration,
                informationFromLoadedBundle: {
                    hotelDiscontinued,
                    originalHotelName,
                    previousEmissionsCo2KgE,
                    originalHotelClass,
                    numberNights,
                }
            }
            if (hotelDurationInNights !== null && day !== null) {
                updateActivityIdsBasedOnDuration(hotelState, hotelDurationInNights, journeyDuration).forEach(updatedHotelState => activitiesForStore.push(updatedHotelState))
            } else {
                activitiesForStore.push(hotelState)
            }
        }
    })
}

const updateActivityIdsBasedOnDuration = (activity: AllActivityTypes, activityDurationInDaysOrNights: number, journeyDuration: DurationProps | null) => {
    const groupId = uuidv4()
    const {common: {activityType}} = activity
    const updatedActivities: AllActivityTypes[] = []
    createArrayFromNumber(activityDurationInDaysOrNights).forEach((_, index) => {
        const updatedActivity = cloneDeep(activity)
        if (updatedActivity.common.day && journeyDuration !== null) {
            updatedActivity.common.day += index
            updatedActivity.common.groupId = groupId
            updatedActivity.common.id = uuidv4()
            switch (activityType) {
                case ACTIVITY_TYPE.car_bus:
                    break;
                case ACTIVITY_TYPE.hotel:
                    if (isActivityHotelState(updatedActivity)) setDurationOfHotelToOneWhenAddingToJourney(updatedActivity, journeyDuration)
                    break;
                case ACTIVITY_TYPE.flight:
                    break
                default:
                    activityType satisfies never
            }
        }
        updatedActivities.push(updatedActivity)
    })
    return updatedActivities
}

const convertFlights = (
    loadedFlightLegs: LoadBundleFlightActivity[],
    activitiesForStore: AllActivityTypesArray,
    activities: LoadedActivities,
    idToGroupIdMapping: Record<number, {
        oneWayGroupId: string
        returnGroupId: string
    }>,
) => {
    let totalEmissions: FlightCalculatedEmissionsResponse[] | null = null
    let originalEmissions: FlightCalculatedEmissionsResponse[] | null = null
    const legsForUi: Leg[] = loadedFlightLegs.map((loadedFlightLeg, legIndex) => {
        const {
            travelClass,
            flightDate,
            flightNumber,
            departureIata,
            departureName,
            aircraftName,
            aircraftIata,
            airlineIata,
            airlineName,
            flightNumberDiscontinued,
            originalAirport,
            departureCountryAlpha3,
            arrivalCountryAlpha3,
            groupId,
            id,
            previousEmissionsCo2KgE,
            returnFlightId,
            emissions
        } = loadedFlightLeg
        if (emissions === null) {
            totalEmissions = [{
                index: legIndex,
                status: CALCULATED_EMISSIONS_RESPONSE_STATUS_PARTIAL.failed,
                emissionKgCO2e: null,
                errorCodes: null,
                errors: null,
                type: CALCULATION_TYPE.flight,
            }]
            originalEmissions = cloneDeep(totalEmissions)
        } else {
            originalEmissions = [cloneDeep(emissions)]
            totalEmissions = [emissions]
        }

        // this is needed to later create the relation between one-way-flight and return flight
        if (returnFlightId !== null) {
            const indexOfReturnLeg = activities.findIndex(activity => activity.id === returnFlightId)
            if (indexOfReturnLeg > -1) {
                const returnActivity = activities[indexOfReturnLeg]
                if (isLoadBundleFlightActivity(returnActivity)) {
                    const returnGroupId = returnActivity.groupId
                    if (returnGroupId !== null) {
                        if (groupId !== null) {
                            idToGroupIdMapping[returnFlightId] = {
                                oneWayGroupId: groupId,
                                returnGroupId
                            }
                        } else {
                            console.warn('groupId is null when loading flight Activity in convertFlights(). This should not be')
                        }
                    }
                } else {
                    console.warn("Activity is not a LoadBundleFlightActivity when calling convertFlights(). This should not happen.")
                }
            }
        }
        const nextLeg = loadedFlightLegs[legIndex + 1]
        let ownAirportTransfer = false
        if (nextLeg) ownAirportTransfer = loadedFlightLeg.arrivalIata !== nextLeg.departureIata
        const departureAirport = {
            airport: {
                id: legIndex,
                iata: departureIata,
                name: departureName,
                country: departureCountryAlpha3,
            },
            travelClass,
            groupId,
            backendId: id,
            id: uuidv4(),
            aircraftType: aircraftIata && aircraftName ? {
                id: legIndex,
                iata: aircraftIata,
                name: aircraftName,
            } : null,
            airline: airlineIata && airlineName ? {
                id: legIndex,
                iata: airlineIata,
                name: airlineName,
            } : null,
            date: convertBackendStringDateToDateTime(flightDate),
            flightNumber,
            ownAirportTransfer,
            informationFromLoadedBundle: {
                flightNumberDiscontinued,
                originalAirport: originalAirport?.departureAirport ?? null,
                previousEmissionsCo2KgE,
            }
        }
        if (legIndex === loadedFlightLegs.length - 1) {
            const {arrivalIata, arrivalName} = loadedFlightLeg
            const lastAirport = {
                airport: {
                    id: legIndex + 1,
                    iata: arrivalIata,
                    name: arrivalName,
                    country: arrivalCountryAlpha3,
                },
                groupId,
                travelClass,
                aircraftType: aircraftIata && aircraftName ? {
                    id: legIndex + 1,
                    iata: aircraftIata,
                    name: aircraftName,
                } : null,
                airline: airlineIata && airlineName ? {
                    id: legIndex + 1,
                    iata: airlineIata,
                    name: airlineName,
                } : null,
                date: convertBackendStringDateToDateTime(flightDate),
                flightNumber,
                backendId: id,
                id: uuidv4(),
                ownAirportTransfer,
                informationFromLoadedBundle: {
                    flightNumberDiscontinued,
                    originalAirport: originalAirport?.arrivalAirport ?? null,
                    previousEmissionsCo2KgE
                }
            }
            return [departureAirport, lastAirport]
        }
        return [departureAirport]
    }).flat()
    const originalRequest: ActivityRequestTypes[] = loadedFlightLegs.map(leg => {
        const {
            index, departureIata, arrivalIata, flightNumber, flightDate, aircraftIata,
            airlineIata, passengerCount, flightCount, travelClass
        } = leg
        return {
            index,
            departure: departureIata,
            arrival: arrivalIata,
            flightNumber,
            flightDate,
            aircraftType: aircraftIata,
            airline: airlineIata,
            passengerCount,
            flightCount,
            travelClass,
            type: CALCULATION_TYPE.flight
        }
    })
    const flightState: ActivityFlightState = {
        common: {
            ...setCommonFlightActivityProps(),
            day: loadedFlightLegs[0].day,
            editId: null,
            hideLoadBundleInformation: false,
            activityType: ACTIVITY_TYPE.flight,
            totalEmissions,
            originalTotalEmissions: originalEmissions,
            id: uuidv4(),
            semanticId: uuidv4(),
            groupId: null,
            originalRequest,
            request: originalRequest,
        },
        passengerCount: loadedFlightLegs[0].passengerCount,
        legs: legsForUi,
        returnId: null,
    }
    activitiesForStore.push(flightState)
}

/**
 * Transforms the persisted flights into the data structure needed by the front end.
 */
const convertLoadedFlightsToFrontEndFlights = (activities: LoadedActivities, activitiesForStore: AllActivityTypesArray) => {
    const flightsGroupedByGroupId = groupBy(activities, "groupId");
    let idToGroupIdMapping: Record<number, {
        oneWayGroupId: string
        returnGroupId: string
    }> = {};
    (Object.keys(flightsGroupedByGroupId) as Array<string>).forEach(flightGroupId => {
        const loadedFlightLegs = flightsGroupedByGroupId[flightGroupId]
        if (loadedFlightLegs.every(isLoadBundleFlightActivity)) {
            const [oneWayFlights, returnFlights] = loadedFlightLegs.reduce<LoadBundleFlightActivity[][]>((acc, flight) => {
                flight.returnFlightId === null ? acc[0].push(flight) : acc[1].push(flight)
                return acc
            }, [[], []])
            oneWayFlights.length > 0 && convertFlights(
                oneWayFlights,
                activitiesForStore,
                activities,
                idToGroupIdMapping,
            )
            returnFlights.length > 0 && convertFlights(
                returnFlights,
                activitiesForStore,
                activities,
                idToGroupIdMapping,
            )
        }
    })

    // This is the creation of the returnId for a flight that has a return flight
    for (const [_, value] of Object.entries(idToGroupIdMapping)) {
        const {
            oneWayGroupId,
            returnGroupId
        } = value
        const returnFlightIndex = activitiesForStore.findIndex(activity => {
            return isActivityFlightState(activity) && activity.legs.every(leg => leg.groupId === returnGroupId)
        })
        if (returnFlightIndex > -1) {
            const returnActivityId = activitiesForStore[returnFlightIndex].common.id
            activitiesForStore.forEach(activity => {
                if (isActivityFlightState(activity) && activity.legs.every(leg => leg.groupId === oneWayGroupId)) activity.returnId = returnActivityId
            })

        }
    }
}

/**
 * Transforms the data from the data structure used in the backend to the data structure that is needed by the
 * frontend in order to display the activities and their associated information.
 */
export const transformBackendBundleToFrontEndBundle = (payload: LoadBundleResponse): BundleForStore => {
    const state = store.getState()
    const {agencies} = state.user
    const {
        activities,
        startDate,
        endDate,
        durationInDays,
        name,
        agencyId,
        customer,
        created,
        description,
        userId,
        updated,
        id,
        previousTotalCo2KgE,
        updatedTotalCo2KgE,
        personCount,
    } = payload
    const agency = agencies?.find(agency => agency.id === agencyId) ?? null
    const activitiesForStore: AllActivityTypesArray = []
    const journeyDuration = convertToDurationProps(startDate, endDate, durationInDays)
    const activitiesGroupedByActivityType = groupBy(activities, "activityType");
    (Object.keys(activitiesGroupedByActivityType) as Array<ACTIVITY_TYPE>).map(activityType => {
        const activities = activitiesGroupedByActivityType[activityType]
        switch (activityType) {
            case ACTIVITY_TYPE.flight:
                convertLoadedFlightsToFrontEndFlights(activities, activitiesForStore)
                break;
            case ACTIVITY_TYPE.hotel:
                convertLoadedHotelsToFrontEndHotels(activities, activitiesForStore, journeyDuration)
                break;
            case ACTIVITY_TYPE.car_bus:
                convertLoadedCarsBussesToFrontEntCarsBusses(activities, activitiesForStore, journeyDuration)
                break;
            default:
                activityType satisfies never;
        }
    })

    return {
        bundleId: id,
        name,
        description,
        agency,
        customer,
        journeyDuration,
        activities: activitiesForStore,
        created: convertBackendStringDateToDateTime(created)!!,
        updated: convertBackendStringDateToDateTime(updated),
        userId,
        previousTotalCo2KgE,
        personCount,
        updatedTotalCo2KgE
    }
}

const setBaseActivityProps = (
    activity: AllActivityTypes,
    id: number | null,
    isUpdate: boolean,
    co2KgE: number | null,
): BaseCreateOrUpdateBundleActivity => ({
    activityType: activity.common.activityType,
    day: activity.common.day,
    co2KgE,
    id: isUpdate ? id : null,
})

const transformFrontEndFlightToBackendFlight = (
    activitiesToPersist: CreateOrUpdatedBundledActivities[],
    activities: AllActivityTypesArray,
    activity: ActivityFlightState,
    request: ActivityRequestTypes,
    totals: TotalCo2KgProps,
    index: number,
    isUpdate: boolean,
    groupId: string,
) => {
    const calculatedFlightEmissions = activity.common.originalTotalEmissions
    if (calculatedFlightEmissions !== null) {
        const {legs} = activity
        const {emissionKgCO2e} = calculatedFlightEmissions[index]
        const {groupId: groupIdFromLoadedBundleActivity} = legs[0]
        if (emissionKgCO2e !== null) totals.totalCo2KgE += emissionKgCO2e
        const departureIndex = index
        const arrivalIndex = index + 1
        const departureLeg = legs[departureIndex]
        const arrivalLeg = legs[arrivalIndex]
        const {backendId} = departureLeg
        if (departureLeg && arrivalLeg) {
            const {airport: departureAirport, airline, aircraftType} = departureLeg
            const {airport: arrivalAirport} = arrivalLeg
            let returnIds: [string, string] | null = null
            const returnActivity = getReturnActivity(activity, activities)
            if (returnActivity && isActivityFlightState(returnActivity)) {
                const {legs: returnLegs} = returnActivity
                const returnLegsLength = returnLegs.length
                const returnDepartureIndex = returnLegsLength - 1 - departureIndex
                const returnArrivalIndex = returnLegsLength - 1 - arrivalIndex
                const returnDepartureLeg = returnLegs[returnDepartureIndex]
                const returnArrivalLeg = returnLegs[returnArrivalIndex]
                returnIds = [returnArrivalLeg.id, returnDepartureLeg.id]
            }
            if (isFlightCalculationRequestLeg(request)) {
                const flightActivity: CreateOrUpdatedFlightActivity = {
                    ...request,
                    ...setBaseActivityProps(activity, backendId, isUpdate, emissionKgCO2e),
                    groupId: groupIdFromLoadedBundleActivity ?? groupId,
                    returnIds,
                    departureName: departureAirport!!.name,
                    arrivalName: arrivalAirport!!.name,
                    departureCountryAlpha3: departureAirport!!.country,
                    arrivalCountryAlpha3: arrivalAirport!!.country,
                    airlineName: airline?.name ?? null,
                    aircraftName: aircraftType?.name ?? null,
                    frontendIds: [departureLeg.id, arrivalLeg.id]
                }
                activitiesToPersist.push(flightActivity)
            } else {
                console.warn('Request is not of type FlightCalculationRequestLeg, which it should be, when converting flight Activity to backend format')
            }
        }
    }
}

const transformFrontEndHotelToBackendHotel = (
    activitiesToPersist: CreateOrUpdatedBundledActivities[],
    activities: AllActivityTypesArray,
    activity: ActivityHotelState,
    request: ActivityRequestTypes,
    totals: TotalCo2KgProps,
    index: number,
    isUpdate: boolean,
    groupIdsAdded: Set<string | null>
) => {
    const {country, hotelStayDuration: {dates}, backendId, common: {groupId, id}} = activity
    const calculatedHotelEmissions = activity.common.originalTotalEmissions
    if (calculatedHotelEmissions && country !== null) {
        const {emissionKgCO2e} = calculatedHotelEmissions[index]
        const {name, alpha2} = country
        const hotelsWithSameGroup = getActivitiesByActivityGroupId(activities, groupId)
        if (isHotelCalculationRequest(request)) {
            const hotelActivity: CreateOrUpdatedHotelActivity = {
                ...request,
                ...setBaseActivityProps(activity, backendId, isUpdate, emissionKgCO2e),
                countryAlpha2: alpha2,
                countryName: name,
                startDate: dates ? convertDateToBackendStringDate(dates.startDate) : null,
                endDate: dates ? convertDateToBackendStringDate(dates.endDate) : null,
                frontendIds: hotelsWithSameGroup ? hotelsWithSameGroup.map(hotelWithSameGroup => hotelWithSameGroup.common.id) : [id],
            }
            // travel component or non-grouped Activity in journey
            // possible to use the request and dates assigned above
            if (groupId === null) {
                activitiesToPersist.push(hotelActivity)
                if (emissionKgCO2e !== null) totals.totalCo2KgE += emissionKgCO2e
            } else {
                if (!groupIdsAdded.has(groupId)) {
                    if (emissionKgCO2e !== null) totals.totalCo2KgE += emissionKgCO2e
                    // if has group, we need to reconstruct the actual Activity since Activities in a journey are
                    // single elements even though they span across multiple days. This is only true for journeys
                    if (hotelsWithSameGroup) {
                        hotelActivity.numberOfNights = hotelsWithSameGroup?.length
                        hotelActivity.day = getStartOrEndDayOfGroupedActivity(groupId, activities, START_OR_END_OF_GROUP.start)
                        const {
                            minValue,
                            maxValue
                        } = getStartAndEndDurationOfHotelStay(activities.filter(isActivityHotelState), activity)
                        if (DateTime.isDateTime(minValue) && DateTime.isDateTime(maxValue)) {
                            hotelActivity.startDate = convertDateToBackendStringDate(minValue)
                            hotelActivity.endDate = convertDateToBackendStringDate(maxValue)
                        }
                        activitiesToPersist.push(hotelActivity)
                    }
                }
            }
            if (groupId !== null) groupIdsAdded.add(groupId)
        }
    } else {
        console.warn('Request is not of type HotelCalculationRequest, which it should be, when converting hotel Activity to backend format')
    }
}

const transformFrontEndCarBusToBackendCarBus = (
    activitiesToPersist: CreateOrUpdatedBundledActivities[],
    activities: AllActivityTypesArray,
    activity: ActivityCarBusState,
    request: ActivityRequestTypes,
    totals: TotalCo2KgProps,
    index: number,
    isUpdate: boolean,
    groupIdsAdded: Set<string | null>
) => {
    const calculatedCarBusEmissions = activity.common.originalTotalEmissions
    const {
        backendId,
        calculatedDistanceKm,
        manualEnteredDistanceKm,
        route,
        waypoints,
        countDays,
        common: {groupId, id}
    } = activity
    if (calculatedCarBusEmissions) {
        const {emissionKgCO2e} = calculatedCarBusEmissions[index]
        const carsBussesWithSameGroup = getActivitiesByActivityGroupId(activities, groupId)
        const carBusActivity: CreateOrUpdateCarBusActivity = {
            ...request as CarBusCalculationRequest,
            daysOfUse: countDays,
            ...setBaseActivityProps(activity, backendId, isUpdate, emissionKgCO2e),
            calculatedDistanceKm,
            manualEnteredDistanceKm,
            route,
            waypoints,
            frontendIds: carsBussesWithSameGroup ? carsBussesWithSameGroup.map(carBusWithSameGroup => carBusWithSameGroup.common.id) : [id],
        }
        // see comment in hotel why this is needed
        if (groupId === null) {
            activitiesToPersist.push(carBusActivity)
            if (emissionKgCO2e !== null) totals.totalCo2KgE += emissionKgCO2e
        } else {
            if (!groupIdsAdded.has(groupId)) {
                if (emissionKgCO2e !== null) totals.totalCo2KgE += emissionKgCO2e
                if (carsBussesWithSameGroup) {
                    carBusActivity.daysOfUse = carsBussesWithSameGroup.length
                    activitiesToPersist.push(carBusActivity)
                }
            }
        }
        if (groupId !== null) groupIdsAdded.add(groupId)
    }
}

/**
 * Transforms the data from the data structure used in the frontend to the data structure that is needed by the
 * backend to persist the activities and their associated information.
 */
export const transformFrontEndBundleToBackendBundle = (payload: CreateBundleProps | UpdateBundle) => {
    const {activities, personCount} = payload
    const isUpdate = isUpdateBundle(payload)
    const activitiesToPersist: CreateOrUpdatedBundledActivities[] = []
    const totals: TotalCo2KgProps = {totalCo2KgE: 0}
    const groupIdsAdded = new Set<string | null>()
    activities.forEach(activity => {
        console.log(activitiesToPersist)
        const groupId = uuidv4()
        return activity.common.request!.map((request, index) => {
            const {common: {activityType}} = activity
            switch (activityType) {
                case ACTIVITY_TYPE.flight:
                    if (isActivityFlightState(activity)) transformFrontEndFlightToBackendFlight(
                        activitiesToPersist,
                        activities,
                        activity,
                        request,
                        totals,
                        index,
                        isUpdate,
                        groupId
                    )
                    break;
                case ACTIVITY_TYPE.hotel:
                    if (isActivityHotelState(activity)) transformFrontEndHotelToBackendHotel(
                        activitiesToPersist,
                        activities,
                        activity,
                        request,
                        totals,
                        index,
                        isUpdate,
                        groupIdsAdded
                    )
                    break;
                case ACTIVITY_TYPE.car_bus:
                    if (isActivityCarBusState(activity)) transformFrontEndCarBusToBackendCarBus(
                        activitiesToPersist,
                        activities,
                        activity,
                        request,
                        totals,
                        index,
                        isUpdate,
                        groupIdsAdded
                    )
                    break;
                default:
                    return activityType satisfies never;
            }
        })
    });

    const {
        name,
        description,
        journeyDuration,
        agency,
        customer,
    } = payload
    let convertedStartDate: string | null = null
    let convertedEndDate: string | null = null
    let journeyDurationInDays: number | null = null
    if (journeyDuration !== null) {
        const {dates, daysOrNights} = journeyDuration
        if (hasExactDates(journeyDuration)) {
            const {startDate, endDate} = dates!!
            convertedStartDate = convertDateToBackendStringDate(startDate)
            convertedEndDate = convertDateToBackendStringDate(endDate)
        }
        journeyDurationInDays = daysOrNights
    }

    if (isUpdate) return {
        id: payload.id,
        name: name.trim(),
        description: description?.trim(),
        agencyId: agency.id,
        customer: isEqual(customer, completeInitialCustomer) ? null : customer,
        startDate: convertedStartDate,
        endDate: convertedEndDate,
        totalCo2KgE: totals.totalCo2KgE,
        durationDays: journeyDurationInDays,
        activities: activitiesToPersist,
        personCount,
    }

    return {
        name: name.trim(),
        description: description?.trim(),
        agencyId: agency.id,
        customer,
        startDate: convertedStartDate,
        endDate: convertedEndDate,
        totalCo2KgE: totals.totalCo2KgE,
        durationDays: journeyDurationInDays,
        activities: activitiesToPersist,
        personCount,
    }
}